import type { UseQueryResult } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import type {
	DokladyDokladDto,
	DokladyDokladSeznamDto,
	OsobyFyzickaOsobaDto,
	OsobySouhlasDto,
	OsobySouhlasSeznamDto,
	ResponseError,
} from '@gov-nx/api';
import {
	consentPreviewsQuery,
	identityDocumentQuery,
	robPersonQuery,
	sendSubmissionQuery,
	useAxiosInstance,
	usePoMutation,
	usePoQueries,
} from '@gov-nx/api';
import { GovError } from '@gov-nx/core/app';
import { useDownloadEvents, useMessageEvents } from '@gov-nx/core/events';
import { useProcessControl, useServiceAuthorization } from '@gov-nx/core/hooks';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { compose, is } from '@gov-nx/core/types';
import { pageRouteResolver } from '@gov-nx/web';
import type { PageCode } from '../../definitions/codes';
import type {
	ConsentManagementBadge,
	ConsentManagementDetailContextTypes,
	ConsentManagementDetailProcessControl,
	ConsentManagementView,
	ConsentRequest,
} from './context.types';
import { prepareSubmitData } from './utils';
import { useForm } from '../useForm';
import { AUTHORIZATION_SERVICE_NAME } from '../constants';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';

const SpravaSouhlasuDetailContext =
	createContext<Nullable<ConsentManagementDetailContextTypes>>(null);

interface SpravaSouhlasuDetailContextProviderProps {
	consentId: number;
	children: React.ReactNode;
	code: PageCode;
}

export function ConsentManagementDetailContextProvider({
	children,
	code,
	consentId,
}: SpravaSouhlasuDetailContextProviderProps) {
	const queryClient = useQueryClient();
	const { controls, setControls } =
		useProcessControl<ConsentManagementDetailProcessControl>({
			initialLoading: true,
			displayModal: false,
			modalProcessLoading: false,
			downloadError: null,
			downloadLoading: false,
		});
	const { downloadBlob } = useDownloadEvents();
	const { toastMessageSuccess, toastMessageError } = useMessageEvents();
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const navigate = useNavigate();
	const authContext = useAutorizaceContext();

	const tsn = getLocalizeCurried(code);
	const [view, setView] = useState<ConsentManagementView>({
		prijemce: true,
		zadatel: false,
		dalsiInformace: false,
		referencniUdaje: true,
		nereferencniUdaje: false,
	});
	const setNewView = (newView: Partial<ConsentManagementView>) =>
		setView({ ...view, ...newView });
	const [consentDetail, setConsentDetail] = useState<OsobySouhlasDto[]>([]);
	const [person, setPerson] = useState<Nullable<OsobyFyzickaOsobaDto>>(null);
	const [idDocuments, setIdDocuments] = useState<DokladyDokladDto[]>([]);

	const [filteredDetail] = consentDetail.filter(
		(item) => item.id === consentId
	);
	const [consentRequestObject, setConsentRequestObject] = useState<
		ConsentRequest | undefined
	>();

	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME);

	const individualBadges: ConsentManagementBadge[] = [
		{
			name: tsn('odznak.prijemce-udaju'),
			isActive: view.prijemce,
			viewFunction: () =>
				setNewView({ prijemce: true, zadatel: false, dalsiInformace: false }),
		},
		{
			name: tsn('odznak.zadatel'),
			isActive: view.zadatel,
			viewFunction: () =>
				setNewView({ zadatel: true, dalsiInformace: false, prijemce: false }),
		},
		{
			name: tsn('odznak.dalsi-informace'),
			isActive: view.dalsiInformace,
			viewFunction: () =>
				setNewView({ dalsiInformace: true, zadatel: false, prijemce: false }),
		},
	];

	const refInfoBadges: ConsentManagementBadge[] = [
		{
			name: tsn('odznak.referencni-udaje'),
			isActive: view.referencniUdaje,
			viewFunction: () =>
				setNewView({ referencniUdaje: true, nereferencniUdaje: false }),
		},
		{
			name: tsn('odznak.nereferencni-udaje'),
			isActive: view.nereferencniUdaje,
			viewFunction: () =>
				setNewView({ nereferencniUdaje: true, referencniUdaje: false }),
		},
	];
	const { axiosInstance } = useAxiosInstance();

	const mainQueries = usePoQueries<
		[
			UseQueryResult<OsobySouhlasSeznamDto, AxiosError<ResponseError>>,
			UseQueryResult<OsobyFyzickaOsobaDto, AxiosError<ResponseError>>,
			UseQueryResult<DokladyDokladSeznamDto, AxiosError<ResponseError>>,
		]
	>({
		queries: [
			{
				queryKey: ['souhlasy'],
				queryFn: async () => consentPreviewsQuery(axiosInstance),
				onError: (error: Error) =>
					setControls({ initialError: new GovError(error.message) }),
			},
			{
				queryKey: ['fyzicke-osoby'],
				queryFn: async () => robPersonQuery(axiosInstance),
				onError: (error: Error) =>
					setControls({ initialError: new GovError(error.message) }),
				refetchOnWindowFocus: false,
				retry: 0,
			},
			{
				queryKey: ['doklady'],
				queryFn: async () => identityDocumentQuery(axiosInstance),
				onError: (error: Error) =>
					setControls({ initialError: new GovError(error.message) }),
				refetchOnWindowFocus: false,
				retry: 0,
			},
		],
	});

	const queriesResponses = mainQueries
		.map((result) => {
			if (result.isError || result.isFetched) {
				return result.data ?? false;
			}
			return undefined;
		})
		.filter(is);

	const isQueriesFetched = queriesResponses.length === mainQueries.length;

	useEffect(() => {
		if (isQueriesFetched && controls.initialLoading) {
			const consents = queriesResponses[0]
				? (queriesResponses[0] as OsobySouhlasSeznamDto)
				: null;
			const robPerson = queriesResponses[1]
				? (queriesResponses[1] as OsobyFyzickaOsobaDto)
				: null;
			const identityDocuments = queriesResponses[2]
				? (queriesResponses[2] as AxiosResponse<DokladyDokladSeznamDto>)
				: null;

			setConsentDetail(consents?.seznam || []);
			setPerson(robPerson);
			setIdDocuments(identityDocuments?.data.seznam || []);
			setControls({ initialLoading: false });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [isQueriesFetched]);

	useEffect(() => {
		if (filteredDetail && idDocuments[0]) {
			setConsentRequestObject({
				kodPodani: 'ZR10_ODVOLANI',
				souhlasId: filteredDetail.id,
				nazevOsoby: filteredDetail.nazevOsoby,
				datovaSchrankaId: filteredDetail.datovaSchrankaId,
				typCisloID: idDocuments[0].cisloDokladu,
			});
		}
	}, [filteredDetail, idDocuments]);

	const handleDownload = useCallback(async () => {
		setControls({
			downloadError: null,
			downloadLoading: true,
			processLoading: true,
		});
		return downloadBlob({
			data: consentRequestObject && prepareSubmitData(consentRequestObject),
			onSuccess: () => {
				setControls({ downloadLoading: false, processLoading: false });
			},
			onError: (error) => {
				setControls({
					downloadError: error,
					downloadLoading: false,
					processLoading: false,
				});
			},
		});
	}, [setControls, downloadBlob, consentRequestObject]);

	const submitMutation = usePoMutation<
		AxiosResponse<number, void>,
		ConsentRequest
	>({
		mutationFn: compose(sendSubmissionQuery(axiosInstance), prepareSubmitData),
		onError: (error) => {
			toastMessageError(tsn('formular.zprava.chyba'));
			setControls({ processError: error, modalProcessLoading: false });
		},
		onSuccess: async (data) => {
			setControls({ modalProcessLoading: false, displayModal: false });
			formDefinition.formMethods.reset();

			const nonHighAuthorizationFlow = async () => {
				toastMessageSuccess(tsn('formular.zprava.souhlas-odvolan'));
				await queryClient.invalidateQueries({ queryKey: ['souhlasy'] });
				navigate(pageRouteResolver('sprava-souhlasu'));
			};

			if (authContext) {
				authContext?.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME,
					data.headers['x-autorizaceid'],
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
	});

	const formDefinition = useForm(
		controls.modalProcessLoading,
		isLowAuthorization
	);

	const handleSubmit = formDefinition.formMethods.handleSubmit(() => {
		setControls({ processError: null, modalProcessLoading: true });
		consentRequestObject && submitMutation.mutate(consentRequestObject);
	});

	const onDialogClose = () => {
		setControls({
			displayModal: false,
		});
		formDefinition.formMethods.reset();
	};

	return (
		<SpravaSouhlasuDetailContext.Provider
			value={{
				controls,
				setControls,
				handleDownload,
				handleSubmit,
				onDialogClose,
				isLowAuthorization,
				isAuthorizationEnforced,
				individualBadges,
				refInfoBadges,
				filteredDetail,
				formDefinition,
				view,
				person,
				idDocuments,
			}}>
			{children}
		</SpravaSouhlasuDetailContext.Provider>
	);
}

export const useSpravaSouhlasuDetailContext =
	(): ConsentManagementDetailContextTypes =>
		useContext(
			SpravaSouhlasuDetailContext
		) as ConsentManagementDetailContextTypes;
